import { getEditor } from "@/utils/useEditor"
import { mat4, vec2 } from "gl-matrix"
import { project, unproject } from "@/core/viewport"
import { radians } from "@/core/utils/math"
import { cursors, pipetteCursor } from "@/components/icons/cursors"
import { PointerEvent, CSSProperties } from "react"
import { degrees } from "@/core/utils/math"
import { getPerspectiveMatrix } from "@/core/perspective"
import {
	getHealingCoordinateTransformCacheKey,
	mapHealingOutputToImageCoord,
	projectHealingImageCoordToOutput,
	projectHealingImageCoordToOutputBestEffort,
} from "@/core/utils/healing-coordinate-transform"
import {
	createHealingTransformBoundsContext,
	HealingTransformBoundsContext,
} from "@/core/utils/healing-transform-bounds"

/**
 * Layer xy coords are 0-1
 * xy represents the center of the layer
 * 0,0 is canvas top-left
 * 1,1 is canvas bottom right
 * 0.5,0.5 is canvas center
 * width and height are also 0-1
 */
export interface Layer {
	x: number
	y: number
	width: number
	height: number
}

export interface Rect {
	left: number
	top: number
	width: number
	height: number
}

interface Bounds {
	left: number
	right: number
	top: number
	bottom: number
}

export type AnchorPoint = keyof typeof anchorPoints

export const anchorPoints = {
	center: [0.5, 0.5],
	left: [0, 0.5],
	right: [1, 0.5],
	top: [0.5, 0],
	bottom: [0.5, 1],
	topLeft: [0, 0],
	topRight: [1, 0],
	bottomRight: [1, 1],
	bottomLeft: [0, 1],
} satisfies Record<string, vec2>

export const layerToBounds = (layer: Layer): Bounds => {
	const halfW = layer.width / 2
	const halfH = layer.height / 2

	return {
		left: layer.x - halfW,
		right: layer.x + halfW,
		top: layer.y - halfH,
		bottom: layer.y + halfH,
	}
}

/**
 * NOTE: Mutates layer directly
 */
export const scaleLayer = (layer: Layer, scale: vec2, origin: vec2, angle?: number) => {
	const pos: vec2 = [layer.x, layer.y]

	if (angle) {
		const rad = radians(angle)

		vec2.rotate(origin, origin, pos, -rad) // Align origin to angle
		vec2.sub(pos, pos, origin) // Align position to origin
		vec2.rotate(pos, pos, [0, 0], rad) // Align position to angle
		vec2.mul(pos, pos, scale) // Scale position
		vec2.rotate(pos, pos, [0, 0], -rad) // Restore position from angle
		vec2.add(pos, pos, origin) // Restore position from origin
	} else {
		vec2.sub(pos, pos, origin) // Align position to origin
		vec2.mul(pos, pos, scale) // Scale position
		vec2.add(pos, pos, origin) // Restore position from origin
	}

	layer.x = pos[0]
	layer.y = pos[1]
	layer.width *= scale[0]
	layer.height *= scale[1]
}

export const getSVGTransform = () => {
	const editor = getEditor()

	const viewMatrix = editor.mainViewport.cropViewMatrix
	const rect = getViewportRect()
	const imageSize = editor.originalImageSize
	const zoom = editor.zoom
	const rotation = editor.rotation
	const flipX = editor.adjustments.flipX
	const flipY = editor.adjustments.flipY

	// Find center of image in viewport
	const [x0, y0] = project([rect.width, rect.height], [0, 0], viewMatrix)

	const imageWidth = (imageSize[0] * zoom) / devicePixelRatio
	const imageHeight = (imageSize[1] * zoom) / devicePixelRatio

	const cx = imageWidth / 2
	const cy = imageHeight / 2

	const sx = flipX ? -1 : 1
	const sy = flipY ? -1 : 1

	const transform = `
      translate(${x0 - cx}, ${y0 - cy})
      rotate(${rotation} ${cx} ${cy})
      matrix(${sx}, 0, 0, ${sy}, ${cx - sx * cx}, ${cy - sy * cy})
    `

	return {
		imageWidth,
		imageHeight,
		transform,
	}
}

/**
 * Calculates position in original image from window/mouse x,y
 */
export const unprojectPointerCoord = (x: number, y: number, invMatrix?: mat4) => {
	const editor = getEditor()
	const rect = getViewportRect()
	const mouse: vec2 = [x - rect.left, y - rect.top]

	const coord = unproject(
		[rect.width, rect.height],
		mouse,
		invMatrix ?? editor.mainViewport.cropViewMatrixInv,
	)

	coord[0] = coord[0] * 0.5 + 0.5
	coord[1] = coord[1] * 0.5 + 0.5

	return coord
}

/**
 * Calculates window/mouse position from position in original image
 */
export const projectPointerCoord = (x: number, y: number, matrix?: mat4) => {
	const editor = getEditor()
	const rect = getViewportRect()

	const coord: vec2 = [x, y]

	coord[0] = coord[0] * 2 - 1
	coord[1] = coord[1] * 2 - 1

	const mouse = project(
		[rect.width, rect.height],
		coord,
		matrix ?? editor.mainViewport.cropViewMatrix,
	)

	mouse[0] += rect.left
	mouse[1] += rect.top

	return mouse
}

let cachedImageTransformContext: HealingTransformBoundsContext | undefined

export const getImageTransformContext = (outputSize?: vec2): HealingTransformBoundsContext => {
	const editor = getEditor()
	const imageSize = editor.photo.imageState.originalImageSize
	const profileManager = editor.renderer.profileManager
	const options = {
		cropMatrix: mat4.create(),
		perspectiveMatrix: getPerspectiveMatrix(
			editor.adjustments,
			editor.photo.initialOrientation,
			imageSize,
		),
		outputSize: outputSize ?? imageSize,
		...profileManager.getLensUniforms(editor.photo),
		renderChromaticAberrationCorrection:
			profileManager.lensChromaticAberrationParams !== null &&
			editor.photo.adjustments.correctLensChromaticAberration,
		chromaticAberrationParams: profileManager.lensChromaticAberrationParams,
	}
	const cacheKey = getHealingCoordinateTransformCacheKey(options)
	if (cachedImageTransformContext?.cacheKey === cacheKey) {
		return cachedImageTransformContext
	}
	cachedImageTransformContext = createHealingTransformBoundsContext(options)
	return cachedImageTransformContext
}

export const mapImageOutputPointsToStable = (points: number[]): number[] => {
	if (points.length % 2 !== 0 || !points.every(Number.isFinite)) return points.slice()

	const context = getImageTransformContext()
	const transformed: number[] = []
	for (let i = 0; i < points.length; i += 2) {
		const point = mapHealingOutputToImageCoord([points[i] ?? 0, points[i + 1] ?? 0], context)
		if (!point) return points.slice()
		transformed.push(point[0], point[1])
	}
	return transformed
}

export const projectImageTransformCoords = (
	points: number[],
	context: HealingTransformBoundsContext,
): number[] | null => {
	if (points.length % 2 !== 0 || !points.every(Number.isFinite)) return null

	const transformed: number[] = []
	for (let index = 0; index < points.length; index += 2) {
		const projected = projectHealingImageCoordToOutput(
			[points[index] ?? 0, points[index + 1] ?? 0],
			context,
		)
		if (!projected) {
			transformed.length = 0
			break
		}
		transformed.push(projected[0], projected[1])
	}
	if (transformed.length === points.length) return transformed

	for (let index = 0; index < points.length; index += 2) {
		const projected = projectHealingImageCoordToOutputBestEffort(
			[points[index] ?? 0, points[index + 1] ?? 0],
			context,
		)
		if (!projected) return points.slice()
		transformed.push(projected[0], projected[1])
	}
	return transformed
}

export const projectImageTransformCoord = (
	coord: vec2,
	context: HealingTransformBoundsContext,
): vec2 | null => {
	const transformed = projectImageTransformCoords([coord[0], coord[1]], context)
	return transformed ? [transformed[0] ?? 0, transformed[1] ?? 0] : null
}

export const getViewportRect = () => {
	const editor = getEditor()
	const canvas = editor.canvas as HTMLCanvasElement
	const viewportRect = editor.mainViewport.viewportRect
	const rect = canvas.getBoundingClientRect()

	return {
		left: rect.left + viewportRect[0] * rect.width,
		top: rect.top + viewportRect[1] * rect.height,
		width: rect.width * viewportRect[2],
		height: rect.height * viewportRect[3],
	}
}

const getCursorAngleSection = (angle: number) => {
	const section = 360 / 8 // split circle into 45deg sections
	const offsetAngle = (angle + section / 2) % 360 // offset by half section
	return Math.floor(offsetAngle / section) // 0-7
}

export const getRotationCursor = (angle: number) => {
	const sectionIndex = getCursorAngleSection(angle)
	const cursorUrl = rotateCursors[sectionIndex]
	return `url("${cursorUrl}") 10 10, default`
}

const getPointerAngle = (e: PointerEvent<SVGElement>, centerX: number, centerY: number) => {
	const [cx, cy] = projectPointerCoord(centerX, centerY)

	const x = e.pageX
	const y = e.pageY
	const dx = x - cx
	const dy = y - cy
	const angle = Math.atan2(dx, dy)

	return 180 - degrees(angle) // degrees clockwise, starts at 0
}

export const setRotationCursor = (
	e: PointerEvent<SVGElement>,
	centerX: number,
	centerY: number,
) => {
	const angle = getPointerAngle(e, centerX, centerY)
	const cursor = getRotationCursor(angle)

	e.currentTarget.style.cursor = cursor
}

export const setResizeCursor = (e: PointerEvent<SVGElement>, centerX: number, centerY: number) => {
	const angle = getPointerAngle(e, centerX, centerY)
	const cursor = getResizeCursor(angle)

	e.currentTarget.style.cursor = cursor
}

export const getColorPipetteCursor = () => {
	return `url("${pipetteCursor}") 0 30, default`
}

export const getResizeCursor = (angle: number) => {
	const sectionIndex = getCursorAngleSection(angle)
	const cursor = resizeCursors[sectionIndex]

	return cursor ?? "default"
}

const resizeCursors: CSSProperties["cursor"][] = [
	"ns-resize", // n
	"nesw-resize", // ne
	"ew-resize", // e
	"nwse-resize", // se
	"ns-resize", // s
	"nesw-resize", // sw
	"ew-resize", // w
	"nwse-resize", // nw
]

const rotateCursors = [
	cursors["cursor_rotate_n"],
	cursors["cursor_rotate_ne"],
	cursors["cursor_rotate_e"],
	cursors["cursor_rotate_se"],
	cursors["cursor_rotate_s"],
	cursors["cursor_rotate_sw"],
	cursors["cursor_rotate_w"],
	cursors["cursor_rotate_nw"],
]
